import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { useMe, usePros } from '../lib/data.ts';
import { first, initials, tone } from '../lib/format.ts';
import { CfgText, Saved } from '../ui/cfg.tsx';
import { BackHeader } from '../ui/Header.tsx';
import { Skeleton } from '../ui/State.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';

interface Role { id: string; key: string; name: string; builtin: boolean; permissions: string[]; people: number }
interface Member { id: string; userId: string; name: string; email: string; active: boolean; roleId: string; roleKey: string; roleName: string; professionalId: string | null }

// as mesmas permissões e grupos do protótipo
export const PERM_GROUPS: Array<[string, Array<[string, string]>]> = [
  ['Agenda', [['agendaTodas', 'Ver e mexer na agenda de todas'], ['cancelar', 'Remarcar e cancelar horários']]],
  ['Dinheiro', [['cobrar', 'Cobrar e receber'], ['mudarPreco', 'Mudar preço e dar desconto (com motivo)'], ['pagarDepois', 'Deixar para pagar depois'], ['estornar', 'Estornar cobranças'], ['valoresSalao', 'Ver o caixa e os valores do salão']]],
  ['Clientes', [['anamnese', 'Ver e preencher a anamnese'], ['juntar', 'Juntar fichas repetidas'], ['dadosLgpd', 'Exportar e apagar dados (LGPD)']]],
  ['Salão', [['relatorios', 'Ver relatórios'], ['config', 'Mudar configurações, equipe e perfis']]],
];
const TOTAL = PERM_GROUPS.reduce((t, g) => t + g[1].length, 0);
const useRoles = () => useQuery({ queryKey: ['roles'], queryFn: () => api.get<{ roles: Role[] }>('/roles') });
const useMembers = () => useQuery({ queryKey: ['members'], queryFn: () => api.get<Member[]>('/members') });

export function Acessos() {
  const nav = useNav();
  const roles = useRoles().data?.roles ?? [];
  const members = useMembers().data ?? [];
  return (
    <div className="view-enter cp2"><BackHeader title="Perfis de acesso" label="Configurações" />
      <div className="cp2-body">
        <p className="ac-intro">Cada pessoa entra com o próprio login e vê só o que o perfil dela permite. Crie perfis do jeito do salão.</p>
        <p className="gl-title">Perfis</p>
        <div className="gl">
          {roles.map((r) => (
            <button key={r.id} className="gl-row gl-btn pa-row" onClick={() => nav.go('perfilAcesso', { id: r.id })}>
              <span className={`ac-role r-${r.key === 'dona' ? 'dona' : r.key === 'parceira' ? 'vitoria' : r.key}`}>{r.name}</span>
              <b>{r.key === 'dona' ? 'Pode tudo' : `${r.permissions.length} de ${TOTAL} permissões`}<small>{r.people ? `${r.people} ${r.people > 1 ? 'pessoas' : 'pessoa'}` : 'ninguém usa ainda'}{r.builtin ? (r.key === 'dona' ? '' : ' · básico') : ' · criado por você'}</small></b><Icon name="chevR" />
            </button>
          ))}
          <button className="gl-row gl-btn ac-invite" onClick={() => nav.open({ kind: 'novoPerfil' })}><b>+ Criar perfil</b><Icon name="chevR" /></button>
        </div>
        <p className="gl-title">Equipe</p>
        <div className="gl">
          {members.map((m) => (
            <button key={m.id} className={'gl-row gl-btn ac-member' + (m.active ? '' : ' ac-pend')} onClick={() => nav.open({ kind: 'membro', id: m.id })}>
              <span className={`av t-${tone(m.userId)}`}>{initials(m.name)}</span><b>{m.name}<small>{m.active ? m.email : 'sem acesso'}</small></b><span className="ac-role">{m.roleName}</span>
            </button>
          ))}
          <button className="gl-row gl-btn ac-invite" onClick={() => nav.open({ kind: 'convite' })}><b>+ Convidar pessoa</b><Icon name="chevR" /></button>
        </div>
        <p className="m-hint">Mudanças valem no próximo toque de quem está usando o app.</p>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

export function PerfilAcesso() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const roles = useRoles().data?.roles ?? [];
  const members = useMembers().data ?? [];
  const r = roles.find((x) => x.id === nav.params.id);
  const [savedAt, setSavedAt] = useState(0);
  const [arm, setArm] = useState(false);
  if (!r) return <div className="view-enter cp2"><BackHeader title="Perfil" label="Perfis" /></div>;
  const dona = r.key === 'dona';
  const save = async (patch: { name?: string; permissions?: string[] }) => {
    try { await api.put(`/roles/${r.id}`, { name: patch.name ?? r.name, permissions: patch.permissions ?? r.permissions }); await qc.invalidateQueries({ queryKey: ['roles'] }); setSavedAt(Date.now()); } catch (e) { toast(msg(e), 'warn'); }
  };
  const quem = members.filter((m) => m.roleId === r.id && m.active);
  return (
    <div className="view-enter cp2"><BackHeader title={r.name} label="Perfis" />
      <div className="cp2-body">
        {dona ? <p className="ac-intro">A dona pode tudo, sempre. Este perfil não muda, para o salão nunca ficar sem quem administre.</p>
          : !r.builtin ? <><p className="gl-title">Nome<Saved at={savedAt} /></p><div className="gl"><CfgText label="Nome do perfil" value={r.name} onChange={(v) => v.length >= 2 && save({ name: v })} /></div></>
          : <p className="gl-title">Permissões<Saved at={savedAt} /></p>}
        {PERM_GROUPS.map(([g, list]) => (
          <div key={g}><p className="gl-title">{g}</p>
            <div className="gl">{list.map(([k, l]) => {
              const on = dona || r.permissions.includes(k);
              return <div key={k} className="gl-row gl-tog"><span>{l}</span><b />{dona ? <em className="mais-ver">sim</em>
                : <button role="switch" aria-checked={on} aria-label={`${r.name}: ${l}`} onClick={() => save({ permissions: on ? r.permissions.filter((x) => x !== k) : [...r.permissions, k] })}><Icon name={on ? 'toggleOn' : 'toggleOff'} /></button>}</div>;
            })}</div></div>
        ))}
        <p className="gl-title">Quem usa</p>
        <div className="gl">{quem.length ? quem.map((m) => (
          <button key={m.id} className="gl-row gl-btn ac-member" onClick={() => nav.open({ kind: 'membro', id: m.id })}><span className={`av t-${tone(m.userId)}`}>{initials(m.name)}</span><b>{m.name}<small>{m.email}</small></b><Icon name="chevR" /></button>
        )) : <p className="m-hint rg-hint" style={{ marginTop: 12 }}>Ninguém usa este perfil ainda. Escolha o perfil de cada pessoa em Equipe.</p>}</div>
        {!dona && (
          <div className="pa-acts">
            <button className="cta ghost" onClick={() => nav.open({ kind: 'novoPerfil', fromId: r.id })}>Duplicar perfil</button>
            {!r.builtin && <button className="link-back pm-del" onClick={async () => {
              if (!arm) return setArm(true);
              try { await api.post(`/roles/${r.id}/remove`); await qc.invalidateQueries({ queryKey: ['roles'] }); nav.back(); toast(`Perfil "${r.name}" excluído.`); } catch (e) { setArm(false); toast(msg(e), 'warn'); }
            }}>{arm ? 'Toque de novo para excluir' : 'Excluir perfil'}</button>}
          </div>
        )}
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

export function NovoPerfilSheet({ fromId }: { fromId?: string }) {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const roles = (useRoles().data?.roles ?? []).filter((r) => r.key !== 'dona');
  const src = roles.find((r) => r.id === fromId);
  const [name, setName] = useState(src ? `${src.name} (cópia)` : '');
  const [base, setBase] = useState<string>(fromId ?? roles.find((r) => r.key === 'parceira')?.id ?? 'zero');
  return (
    <Sheet title="Criar perfil" actions={<><button className="cta" disabled={name.trim().length < 2} onClick={async () => {
      try {
        const perms = base === 'zero' ? [] : roles.find((r) => r.id === base)?.permissions ?? [];
        const r = await api.post<{ id: string }>('/roles', { name: name.trim(), permissions: perms });
        await qc.invalidateQueries({ queryKey: ['roles'] }); nav.go('perfilAcesso', { id: r.id });
        toast(`Perfil "${name.trim()}" criado. Ajuste o que ele pode fazer.`);
      } catch (e) { toast(msg(e), 'warn'); }
    }}>Criar perfil</button><button className="link-back" onClick={nav.close}>Cancelar</button></>}>
      <div className="field"><label htmlFor="npNome">Nome do perfil</label><input className="text-in" id="npNome" autoFocus autoComplete="off" placeholder="Ex.: Gerente, Manicure iniciante" value={name} onChange={(e) => setName(e.target.value)} /></div>
      <p className="gl-title">Começar</p>
      <div className="chipset soft">
        {roles.map((r) => <button key={r.id} className={base === r.id ? 'on' : ''} aria-pressed={base === r.id} onClick={() => setBase(r.id)}>Igual a {r.name}</button>)}
        <button className={base === 'zero' ? 'on' : ''} aria-pressed={base === 'zero'} onClick={() => setBase('zero')}>Do zero</button>
      </div>
      <p className="m-hint">Depois de criar, você liga e desliga cada permissão.</p>
    </Sheet>
  );
}

export function MembroSheet({ id }: { id: string }) {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const roles = (useRoles().data?.roles ?? []).filter((r) => r.key !== 'dona');
  const m = (useMembers().data ?? []).find((x) => x.id === id);
  const pros = usePros().data ?? [];
  const [pw, setPw] = useState('');
  const [armOff, setArmOff] = useState(false);
  if (!m) return <Sheet title="Pessoa"><Skeleton kind="sheet" rows={3} /></Sheet>;
  if (m.roleKey === 'dona') return <Sheet title={m.name} sub={m.email}><p className="m-hint">A dona sempre tem acesso a tudo.</p></Sheet>;
  const upd = async (patch: Partial<Pick<Member, 'roleId' | 'professionalId' | 'active'>>, ok: string) => {
    try { await api.put(`/members/${m.id}`, { roleId: m.roleId, professionalId: m.professionalId, active: m.active, ...patch }); await qc.invalidateQueries({ queryKey: ['members'] }); await qc.invalidateQueries({ queryKey: ['roles'] }); toast(ok); } catch (e) { toast(msg(e), 'warn'); }
  };
  return (
    <Sheet title={m.name} sub={m.email} actions={<button className="link-back" onClick={nav.close}>Voltar</button>}>
      <p className="gl-title first">Perfil</p>
      <div className="gl">{roles.map((r) => (
        <button key={r.id} className={'gl-row gl-btn ac-pick' + (m.roleId === r.id ? ' on' : '')} onClick={() => upd({ roleId: r.id }, `${first(m.name)} agora usa o perfil ${r.name}.`)}>
          <span>{r.name}</span><b>{r.permissions.length} permissões</b>{m.roleId === r.id ? <Icon name="check" /> : null}</button>
      ))}</div>
      <p className="gl-title">Na agenda, ela é</p>
      <div className="chipset soft">
        <button className={!m.professionalId ? 'on' : ''} onClick={() => upd({ professionalId: null }, `${first(m.name)} não tem agenda própria.`)}>Sem agenda (recepção)</button>
        {pros.filter((p) => p.active).map((p) => <button key={p.id} className={m.professionalId === p.id ? 'on' : ''} onClick={() => upd({ professionalId: p.id }, `${first(m.name)} ligada à agenda de ${p.name}.`)}>{p.name}</button>)}
      </div>
      <p className="gl-title">Senha</p>
      <div className="field"><label htmlFor="mbPw">Nova senha provisória (pelo menos 10 caracteres)</label><input className="text-in" id="mbPw" type="text" autoComplete="off" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
      <button className="cta ghost" disabled={pw.length < 10} onClick={async () => { try { await api.post(`/members/${m.id}/password`, { password: pw }); setPw(''); toast(`Senha de ${first(m.name)} redefinida. Passe a nova senha para ela; ela pode trocar em Meu perfil.`); } catch (e) { toast(msg(e), 'warn'); } }}>Redefinir senha</button>
      <p className="gl-title">Acesso</p>
      {m.active
        ? <button className="link-back pm-del" onClick={() => (armOff ? upd({ active: false }, `${first(m.name)} não entra mais no app. As sessões abertas foram encerradas.`) : setArmOff(true))}>{armOff ? 'Toque de novo para tirar o acesso' : 'Tirar o acesso ao app'}</button>
        : <button className="cta ghost" onClick={() => upd({ active: true }, `${first(m.name)} voltou a ter acesso.`)}>Devolver o acesso</button>}
    </Sheet>
  );
}

export function ConviteSheet() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const roles = (useRoles().data?.roles ?? []).filter((r) => r.key !== 'dona');
  const pros = (usePros().data ?? []).filter((p) => p.active && !p.isOwner);
  const [f, setF] = useState({ name: '', email: '', password: '', roleId: '', agenda: 'nova' as string });
  const roleId = f.roleId || roles.find((r) => r.key === 'parceira')?.id || '';
  // com o e-mail ligado, a pessoa recebe o link e cria a própria senha; senão, a dona define uma provisória
  const me = useMe().data!;
  const byEmail = !!me.features?.email && !f.password;
  const ok = f.name.trim().length > 1 && /.+@.+\..+/.test(f.email) && (byEmail || f.password.length >= 10) && roleId;
  return (
    <Sheet title="Convidar pessoa" sub="Equipe da Sublime" actions={<><button className="cta" disabled={!ok} onClick={async () => {
      try {
        const r = await api.post<{ emailSent: boolean }>('/members', { name: f.name.trim(), email: f.email.trim(), password: f.password || undefined, roleId, professionalId: f.agenda !== 'nova' && f.agenda !== 'nenhuma' ? f.agenda : null, createProfessional: f.agenda === 'nova' });
        await qc.invalidateQueries({ queryKey: ['members'] }); await qc.invalidateQueries({ queryKey: ['professionals'] }); await qc.invalidateQueries({ queryKey: ['roles'] });
        nav.close();
        if (f.password) toast(`${first(f.name)} pode entrar com ${f.email} e a senha provisória. Ela troca em Meu perfil.`);
        else if (r.emailSent) toast(`Convite enviado para ${f.email}. ${first(f.name)} cria a senha pelo link do e-mail.`);
        else toast('Acesso criado, mas o e-mail não saiu. Defina uma senha nova para ela no perfil dela.', 'warn');
      } catch (e) { toast(msg(e), 'warn'); }
    }}>Criar acesso</button><button className="link-back" onClick={nav.close}>Voltar</button></>}>
      <div className="field"><label htmlFor="cvName">Nome</label><input className="text-in" id="cvName" autoFocus autoComplete="off" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Como ela se chama" /></div>
      <div className="field"><label htmlFor="cvEmail">E-mail</label><input className="text-in" id="cvEmail" type="email" autoComplete="off" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="ela@exemplo.com" /></div>
      <div className="field"><label htmlFor="cvPw">{me.features?.email ? 'Senha provisória (opcional)' : 'Senha provisória (pelo menos 10 caracteres)'}</label><input className="text-in" id="cvPw" type="text" autoComplete="off" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
      <p className="gl-title">Perfil</p>
      <div className="chipset soft">{roles.map((r) => <button key={r.id} className={roleId === r.id ? 'on' : ''} onClick={() => setF({ ...f, roleId: r.id })}>{r.name}</button>)}</div>
      <p className="gl-title">Agenda</p>
      <div className="chipset soft">
        <button className={f.agenda === 'nova' ? 'on' : ''} onClick={() => setF({ ...f, agenda: 'nova' })}>Agenda nova com o nome dela</button>
        {pros.map((p) => <button key={p.id} className={f.agenda === p.id ? 'on' : ''} onClick={() => setF({ ...f, agenda: p.id })}>É a {p.name}</button>)}
        <button className={f.agenda === 'nenhuma' ? 'on' : ''} onClick={() => setF({ ...f, agenda: 'nenhuma' })}>Sem agenda (recepção)</button>
      </div>
      <p className="m-hint">{me.features?.email ? 'Sem senha provisória, ela recebe um e-mail de boas-vindas com o link para criar a senha dela.' : 'Passe o e-mail e a senha provisória para ela. No primeiro acesso, ela troca a senha em Meu perfil.'}</p>
    </Sheet>
  );
}
